import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { serverStore } from '../../stores/server.store';
import { ServerDiagnosisProgress } from '../ai/ServerDiagnosisProgress';
import { FirewallPortsCard } from '../server/FirewallPortsCard';
import { ServerStatTiles } from '../server/ServerStatTiles';
import { PlayerSummaryCard } from '../server/PlayerSummaryCard';
import { QuickActions } from '../server/QuickActions';
import { QuickSettingsCard } from '../server/QuickSettingsCard';
import { SteamHero } from '../server/steam/SteamHero';
import { WorldInfoCard } from '../server/steam/WorldInfoCard';
import { ActivityCard } from '../server/steam/ActivityCard';
import type { SteamOverview } from '../server/steam/types';
import { formatBytes } from '../../utils/overview-format';

const POLL_MS = 15000;

/**
 * Übersicht für Steam-Server (Entwurf „A · Spielbühne", 2026-09-14): Bühne mit
 * Spielbild (bereit? beitreten? wer ist online?), die gewohnten Kacheln,
 * Spieler + „Welt & Server" neben Schnelleinstellungen und Quick Actions,
 * darunter die Aktivität. Daten: GET /steam/overview (Bereit-Status, Save,
 * Backup, A2S) alle 15 s.
 */
export const SteamDashboard = observer(() => {
  const { activeServer } = serverStore;
  const id = activeServer?.id;
  const status = activeServer?.status;
  const [overview, setOverview] = useState<SteamOverview | null>(null);
  const [fw, setFw] = useState<any>(null);

  useEffect(() => { setOverview(null); }, [id]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    const load = async () => {
      const o = await serverStore.fetchSteamOverview(id);
      if (alive && o) setOverview(o);
      if (serverStore.activeServer?.status === 'online') serverStore.fetchRuntimeDetails(id);
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [id, status]);

  // Firewall nur beim Öffnen prüfen - jeder Check ist ein sudo-Aufruf.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setFw(null);
    serverStore.fetchFirewall(id).then((d) => { if (alive) setFw(d); });
    return () => { alive = false; };
  }, [id]);

  if (!activeServer) return null;
  const online = status === 'online';
  const names: string[] = online ? (serverStore.serverRuntimeDetails.get(activeServer.id)?.playerCount?.players || []) : [];
  const portsMissing = fw && fw.checked && !fw.allOpen;

  const saveMaxPlayers = async (n: number) => {
    const ok = await serverStore.saveSteamConfig(activeServer.id, { maxPlayers: n }, { silent: true });
    if (ok) setOverview((o) => (o ? { ...o, maxPlayers: n } : o));
    return { ok, restartNeeded: serverStore.activeServer?.status === 'online' };
  };

  return (
    <div className="space-y-6">
      <ServerDiagnosisProgress />

      {overview
        ? <SteamHero overview={overview} server={activeServer} firewall={fw} playerNames={names} />
        : <div className="h-[272px] rounded-2xl border border-line bg-surface animate-pulse" />}

      {portsMissing && <FirewallPortsCard serverId={activeServer.id} />}

      <ServerStatTiles diskSub={overview?.saveBytes ? `Installation · Spielstand ${formatBytes(overview.saveBytes)}` : undefined} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-6">
          {online && <PlayerSummaryCard />}
          {overview && <WorldInfoCard overview={overview} online={online} serverId={activeServer.id} />}
        </div>
        <div className="space-y-6">
          <QuickSettingsCard maxPlayers={overview ? overview.maxPlayers ?? null : null} onSaveMaxPlayers={saveMaxPlayers} maxPlayersLimit={255} />
          {online && <QuickActions />}
        </div>
      </div>

      <ActivityCard />
    </div>
  );
});
